'use client';

import cn from 'classnames';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useMusiciansMutation } from '@shared/api';
import type { Locale } from '@shared/libs';
import type { MusiciansQueryResponse } from '@shared/types';
import { Pagination, ShowMore } from '@shared/ui';
import styles from './musicians-table.module.css';

type TProps = MusiciansQueryResponse & {
  locale: Locale;
  page?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  alpha?: string;
};

export const MusiciansTable = ({
  data,
  pager,
  locale,
  page,
  alpha,
}: TProps) => {
  const { records_all, records_per_page } = pager;
  const isMobile = useMediaQuery({ maxWidth: 480 });

  const [localPage, setLocalPage] = useState(page || 1);
  useEffect(() => {
    setLocalPage(page || 1);
  }, [page]);

  const moreResultCount = records_all - localPage * records_per_page;

  const [localData, setLocalData] = useState(data);
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const moreMutation = useMusiciansMutation();

  const handleShowMore = async () => {
    const { data: newData } = await moreMutation.mutateAsync({ page: localPage + 1, alpha });
    setLocalPage((prev) => prev + 1);
    setLocalData((prev) => [...prev, ...newData]);
  };

  return (
    <>
      <table className={styles.table}>
        <thead className={styles.head}>
          <tr className={styles.row}>
            <th className={cn(styles.cell, styles.titleCell)}>{locale.MUSICIAN_TABLE_NAME}</th>
            <th className={cn(styles.cell, styles.titleCell)}>{locale.MUSICIAN_TABLE_GENRE}</th>
            <th className={cn(styles.cell, styles.titleCell)}>
              {isMobile
                ? locale.MUSICIAN_TABLE_COUNT_NOTES_MOBILE
                : locale.MUSICIAN_TABLE_COUNT_NOTES}
            </th>
          </tr>
        </thead>
        <tbody>
          {localData?.map(({ id, title, genres, url, notes_count }) => (
            <tr key={id} className={styles.row}>
              <td className={cn(styles.cell, styles.data)}>
                <Link href={url} prefetch={false}>
                  {title}
                </Link>
              </td>
              <td className={cn(styles.cell, styles.data)}>
                {genres.map(({ id: idGenres, title: titleGenres, smart_url }) => (
                  <Link
                    key={idGenres || ''}
                    href={smart_url || '/'}
                    className={styles.link}
                    prefetch={false}
                  >
                    {titleGenres}
                  </Link>
                ))}
              </td>
              <td className={cn(styles.cell, styles.data)}>{notes_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ShowMore
        moreResultCount={moreResultCount}
        isLoading={moreMutation.isLoading}
        text={locale.SHOW_MORE || 'SHOW_MORE'}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        handleClick={handleShowMore}
      />
      <Pagination locale={locale} pager={{ ...pager, page_current: localPage }} query="PAGEN_2" />
    </>
  );
};
